import { Typography } from 'antd';
import { Formik } from 'formik';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ModulesOptionsDataFragment } from '../../api/fragments/ModulesOptionsData';
import { CompanyTheme, type ModuleType } from '../../api/types';
import { useThemeComponents } from '../../themes';
import useHandleError from '../../utilities/useHandleError';
import Form from '../fields/Form';
import ModuleSelectField from '../fields/ModuleSelectField';
import CancelButton from '../modal/CancelButton';
import OkButton from '../modal/OkButton';
import { ContentContainer } from './ui';

export type UseModuleSelectionModalProps = {
    title: string;
    moduleType: ModuleType;
    onFinish?: (moduleId: string) => void;
    availableModules?: ModulesOptionsDataFragment[];
};

export type ModuleSelectionModalProps = Omit<UseModuleSelectionModalProps, 'onFinish'> & {
    isOpen: boolean;
    onClose: (moduleId?: string) => void;
};

const ModuleSelectionModal = ({ title, moduleType, onClose, isOpen, availableModules }: ModuleSelectionModalProps) => {
    const { t } = useTranslation(['importExportExcel']);
    const { theme, Modal } = useThemeComponents();

    const onCancelHandler = useCallback(
        (handleReset: () => void) => {
            handleReset();

            onClose();
        },
        [onClose]
    );

    const onSubmitHandler = useHandleError(
        async (values, { resetForm }) => {
            const { moduleId } = values;

            resetForm();
            onClose(moduleId);
        },
        [onClose]
    );

    const useFooter = useCallback(
        (handleReset: () => void) => {
            const cancelButton = (
                <CancelButton key="cancel" onClick={() => onCancelHandler(handleReset)}>
                    {t('importExportExcel:actions.cancel')}
                </CancelButton>
            );

            const okButton = (
                <OkButton key="submit" form="moduleSelectionForm" htmlType="submit">
                    {t('importExportExcel:actions.continue')}
                </OkButton>
            );

            if (theme === CompanyTheme.PorscheV3 || theme === CompanyTheme.Porsche) {
                return [okButton, cancelButton];
            }

            return [cancelButton, okButton];
        },
        [onCancelHandler, theme, t]
    );

    return (
        <Formik initialValues={{}} onSubmit={onSubmitHandler}>
            {({ handleSubmit, handleReset }) => (
                <Modal
                    footer={useFooter(handleReset)}
                    onCancel={() => onCancelHandler(handleReset)}
                    open={isOpen}
                    title={title}
                    width={550}
                >
                    <Form id="moduleSelectionForm" name="moduleSelectionForm" onSubmitCapture={handleSubmit}>
                        <ContentContainer direction="vertical" size="middle">
                            <Typography.Text>{t('importExportExcel:description.mutipleModules')}</Typography.Text>
                            <ModuleSelectField
                                moduleType={moduleType}
                                modules={availableModules}
                                name="moduleId"
                                required
                            />
                        </ContentContainer>
                    </Form>
                </Modal>
            )}
        </Formik>
    );
};

const useModuleSelectionModal = ({ title, moduleType, onFinish, availableModules }: UseModuleSelectionModalProps) => {
    const [isOpenModal, setOpenModal] = useState(false);
    const [selectedModuleId, setModuleId] = useState<string | null>(null);

    const onClose = useCallback(
        (moduleId?: string) => {
            if (moduleId) {
                setModuleId(moduleId);
            }
            setOpenModal(false);

            if (onFinish) {
                onFinish(moduleId);
            }
        },
        [onFinish]
    );

    return {
        isOpenModal,
        setOpenModal,
        selectedModuleId,
        render: () => (
            <ModuleSelectionModal
                availableModules={availableModules}
                isOpen={isOpenModal}
                moduleType={moduleType}
                onClose={onClose}
                title={title}
            />
        ),
    };
};

export default useModuleSelectionModal;
